import { useLayoutEffect, useRef, useState } from 'react'
import { formatTooltipTimestamp } from '../../socket/useTelemetryHistory'


const formatValue = (value, unit) => (value != null ? `${value}${unit}` : '-')

const VIEWPORT_MARGIN = 8


const ROW_HEIGHT = 24 // text-sm leading-5 + gap-y-1
const HEADER_HEIGHT = 56 // timestamp line + the card's own padding
const COLUMN_WIDTH = 132 // chip + "Cell 32" + "4.186 V" + column gap

// One straight column for as long as the window is tall enough to hold every
// series — 32 cells fit on any normal screen. Wrapping to a second column is
// the fallback for a short window only; columns cost width the chart needs
// more than the tooltip does.
const planLayout = (count) => {
  const maxRows = Math.max(1, Math.floor((window.innerHeight - VIEWPORT_MARGIN * 2 - HEADER_HEIGHT) / ROW_HEIGHT))
  const maxColumns = Math.max(1, Math.floor((window.innerWidth - VIEWPORT_MARGIN * 2) / COLUMN_WIDTH))
  const columns = Math.min(maxColumns, Math.ceil(count / maxRows))
  return { rows: Math.ceil(count / columns) }
}

const useViewportClamp = () => {
  const ref = useRef(null)
  const offsetRef = useRef({ x: 0, y: 0 })
  const [offset, setOffset] = useState({ x: 0, y: 0 })

  
  // rule can't see that, hence the disable.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const applied = offsetRef.current
    const top = rect.top - applied.y
    const left = rect.left - applied.x
    
    const maxTop = Math.max(VIEWPORT_MARGIN, window.innerHeight - VIEWPORT_MARGIN - rect.height)
    const maxLeft = Math.max(VIEWPORT_MARGIN, window.innerWidth - VIEWPORT_MARGIN - rect.width)
    const next = {
      x: Math.min(Math.max(left, VIEWPORT_MARGIN), maxLeft) - left,
      y: Math.min(Math.max(top, VIEWPORT_MARGIN), maxTop) - top,
    }
    
    if (next.x !== applied.x || next.y !== applied.y) {
      offsetRef.current = next
      setOffset(next)
    }
  })

  return [ref, offset]
}


const ChartTooltip = ({ active, payload, label, legend = [], hiddenSeries = [] }) => {
  const [ref, offset] = useViewportClamp()
  if (!active || !payload?.length) return null
  const visible = payload.filter((entry) => !hiddenSeries.includes(entry.dataKey))
  if (!visible.length) return null
  const unitByName = Object.fromEntries(legend.map((s) => [s.name, s.unit ?? '']))
  const { rows } = planLayout(visible.length)

  return (
    <div
      ref={ref}
      className="bg-[#f9fafa] rounded-lg p-3 flex flex-col gap-2 shadow-[0px_0px_8px_1px_rgba(22,28,39,0.15)]"
      style={{ transform: `translate(${offset.x}px, ${offset.y}px)`, maxWidth: `calc(100vw - ${VIEWPORT_MARGIN * 2}px)` }}
    >
      <p className="text-sm font-normal text-[#272d37] leading-5 whitespace-pre-wrap shrink-0">{formatTooltipTimestamp(label)}</p>
      <div
        className="grid gap-x-5 gap-y-1 overflow-y-auto"
        style={{
          gridAutoFlow: 'column',
          gridTemplateRows: `repeat(${rows}, auto)`,
          gridAutoColumns: 'minmax(0, 1fr)',
          maxHeight: `calc(100vh - ${HEADER_HEIGHT + VIEWPORT_MARGIN * 2}px)`,
        }}
      >
        {visible.map((entry) => (
          <div key={entry.dataKey} className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-1 min-w-0">
              <span className="w-3.5 h-3.5 rounded-sm shrink-0" style={{ backgroundColor: entry.color }} />
              <span className="text-sm font-normal text-[#616a79] whitespace-nowrap">{entry.dataKey}</span>
            </span>
            <span className="text-sm font-normal text-[#272d37] text-right whitespace-nowrap">{formatValue(entry.value, unitByName[entry.dataKey])}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

export default ChartTooltip
